import React, { useMemo, useState } from "react";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import Select from "react-select";
import moment from "moment";
import { LuAlertCircle, LuBuilding2, LuDatabase } from "react-icons/lu";
import { toast } from "react-toastify";

import CuttingItemPickerModal, {
  CuttingCardProps,
  CuttingCardSize,
} from "./CuttingItemPickerModal";
import CuttingMaterialsSent, {
  CuttingMaterialNeed,
  barsPayload,
  materialCut,
  skipPayload,
} from "./CuttingMaterialsSent";
import CuttingOrderLines, { CuttingLineView } from "./CuttingOrderLines";
import {
  CuttingCategoryOption,
  categoryText,
  useCategoryBoxes,
} from "../cuttingLines/CuttingCategoryBoxes";
import Button from "../ui/Button";
import Input from "../ui/Input";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  cardContentClass,
  fieldErrorIconClass,
  fieldErrorTextClass,
  fieldInputClass,
  fieldLabelClass,
  formCardClass,
  formSelectIconClass,
  gradientSubmitButtonClass,
} from "../ui/styles";
import { useAppSelector } from "../../app/hooks";
import { getSizeUnit } from "../../features/appSettings/appSettingsSlice";
import {
  cuttingLineKey,
  effectiveQty,
  formatLength,
  lineName,
  optionKey,
  pieceLength,
  rateSourceLabel,
  toUnits,
} from "../../static/cuttingLine";
import { formatAmount } from "../../static/materialLine";
import { rateNumber, rateText } from "../../static/processRate";
import { iconSelectStyles, toastStyles } from "../../static/styles";
import {
  CuttingBarsPayload,
  CuttingCategoryProps,
  CuttingMaterialInput,
  CuttingOptionProps,
  CuttingOptionSizeProps,
  CuttingOrderItemProps,
  CuttingOrderProps,
  CuttingPartyInfoProps,
  CuttingSkipPieceProps,
  ICuttingLineInput,
  ICuttingOrderFormInput,
} from "../../types/cuttingOrder";
import {
  MaterialOptionProps,
  PartyOptionProps,
} from "../../types/materialLine";

type Props = {
  defaultValues?: ICuttingOrderFormInput;
  // What an order being edited did with each material: the bars sent and the party's pieces it left alone.
  defaultMaterials?: Record<number, CuttingMaterialInput>;
  partyOptions: PartyOptionProps[];
  partyInfo: CuttingPartyInfoProps;
  partyInfoLoading: boolean;
  materials: MaterialOptionProps[];
  // The order being edited, with what each of its lines copied and has received.
  savedOrder?: CuttingOrderProps;
  // An order with receives keeps its party.
  partyLocked?: boolean;
  // Called when another party is picked, so the page can load that party's items and material.
  onPartyChange: (partyId: number) => void;
  onSubmit: (
    data: ICuttingOrderFormInput,
    materials: CuttingBarsPayload[],
    skipPieces: CuttingSkipPieceProps[]
  ) => void;
  isSubmitting: boolean;
  onCancel: () => void;
  submitLabel: string;
  // Rates only for a user who may see them.
  withRates: boolean;
};

type OptionEntry = { option: CuttingOptionProps; size: CuttingOptionSizeProps };

const noInput: CuttingMaterialInput = { skip: [], sizeId: null, qty: null };

// The form of a cutting order: the party, the items to cut from the picker, and the material they need: the party's
// pieces to use and the bars sent along, at least as many as the lines need. A saved line keeps the cut length and rate
// it was added with; the server copies them for new lines.
const CuttingOrderForm = ({
  defaultValues,
  defaultMaterials,
  partyOptions,
  partyInfo,
  partyInfoLoading,
  materials,
  savedOrder,
  partyLocked,
  onPartyChange,
  onSubmit,
  isSubmitting,
  onCancel,
  submitLabel,
  withRates,
}: Props) => {
  const sizeUnit = useAppSelector(getSizeUnit);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    clearErrors,
    formState: { errors },
  } = useForm<ICuttingOrderFormInput>({
    defaultValues: defaultValues || {
      party_id: 0,
      date: moment().format("YYYY-MM-DDTHH:mm"),
      items: [],
    },
  });

  const { fields, remove, replace } = useFieldArray({ control, name: "items" });

  const partyId = useWatch({ control, name: "party_id" });
  const watchedItems = useWatch({ control, name: "items" });
  const items = useMemo(() => watchedItems || [], [watchedItems]);

  const [materialInputs, setMaterialInputs] = useState<
    Record<number, CuttingMaterialInput>
  >(() => defaultMaterials || {});
  // The category whose items the picker shows, while it is open.
  const [pickerCategoryId, setPickerCategoryId] = useState<number | null>(
    null
  );
  const [showBoxErrors, setShowBoxErrors] = useState(false);

  // Each saved line's category, even once the party changed (the item keeps its category).
  const savedCategoryOfItem = useMemo(
    () =>
      new Map(
        (savedOrder?.items || []).map((item) => [
          item.item_id,
          item.item?.category_id ?? 0,
        ])
      ),
    [savedOrder]
  );

  const optionByKey = useMemo(() => {
    const entries = new Map<string, OptionEntry>();

    partyInfo.options.forEach((option) =>
      option.sizes.forEach((size) =>
        entries.set(optionKey(option.item_id, size.size_id), { option, size })
      )
    );

    return entries;
  }, [partyInfo]);

  // The order's own lines count only while it keeps its party: another party copies every line again.
  const partyChanged = !!savedOrder && Number(partyId) !== savedOrder.party_id;

  const savedByKey = useMemo(() => {
    const lines = new Map<string, CuttingOrderItemProps>();

    if (savedOrder && !partyChanged) {
      savedOrder.items.forEach((item) => lines.set(cuttingLineKey(item), item));
    }

    return lines;
  }, [savedOrder, partyChanged]);

  const materialById = useMemo(
    () => new Map(materials.map((material) => [material.id, material])),
    [materials]
  );

  // The saved lines' ids, even once the party changed: the backend keeps a line's row, and cuts lines of one length in
  // that order.
  const savedLineIds = useMemo(
    () =>
      new Map(
        (savedOrder?.items || []).map((item) => [cuttingLineKey(item), item.id])
      ),
    [savedOrder]
  );

  // Bars of more sizes of one material that an order saved before one size per material sent: the form keeps the first
  // (as the edit page loads it), so a save drops the rest.
  const droppedBars = useMemo(() => {
    const dropped = new Map<number, string[]>();
    const seen = new Set<number>();

    (savedOrder?.materials || []).forEach((sent) => {
      if (seen.has(sent.material_id)) {
        dropped.set(sent.material_id, [
          ...(dropped.get(sent.material_id) || []),
          `${pieceLength(sent.length, sizeUnit)} × ${sent.qty}`,
        ]);
      } else {
        seen.add(sent.material_id);
      }
    });

    return dropped;
  }, [savedOrder, sizeUnit]);

  // The picker's cards: one per item the party can be asked to cut, with its sizes, and the order's own lines it no
  // longer offers, in their item's card or a card of their own. A size the order has shows what its line copied.
  const cards = useMemo((): CuttingCardProps[] => {
    const byItem = new Map<number, CuttingCardProps>();

    partyInfo.options.forEach((option) =>
      byItem.set(option.item_id, {
        key: String(option.item_id),
        item_id: option.item_id,
        categoryId: option.category?.id ?? 0,
        code: option.code,
        subtitle: option.parent
          ? `મુખ્ય આઈટમ: ${option.parent.name}`
          : option.description,
        editItemId: option.parent?.id ?? option.item_id,
        imageUrl: option.image_url,
        materialName: option.material?.name ?? null,
        sizes: option.sizes.map((size): CuttingCardSize => {
          const key = optionKey(option.item_id, size.size_id);
          const saved = savedByKey.get(key);

          return {
            key,
            size_id: size.size_id,
            sizeName: size.size_name,
            cutLength: saved ? saved.cut_length : size.cut_length,
            rate: saved ? saved.rate : size.rate,
            rateSource: saved ? saved.rate_source : size.rate_source,
            pending: size.pending_qty,
            received: saved?.received_qty || 0,
            closed: !!saved?.closed,
          };
        }),
      })
    );

    savedByKey.forEach((saved, key) => {
      if (optionByKey.has(key)) return;

      const card = byItem.get(saved.item_id) || {
        key: String(saved.item_id),
        item_id: saved.item_id,
        categoryId: saved.item?.category_id ?? 0,
        code: saved.item?.name || `#${saved.item_id}`,
        subtitle: saved.item?.parent
          ? `મુખ્ય આઈટમ: ${saved.item.parent.name}`
          : saved.item?.description ?? null,
        editItemId: saved.item?.parent?.id ?? saved.item_id,
        imageUrl: saved.item?.image_url ?? null,
        materialName: saved.material?.name ?? null,
        sizes: [],
      };

      card.sizes.push({
        key,
        size_id: saved.size_id,
        sizeName: saved.size?.name ?? null,
        cutLength: saved.cut_length,
        rate: saved.rate,
        rateSource: saved.rate_source,
        pending: 0,
        received: saved.received_qty || 0,
        closed: !!saved.closed,
      });
      byItem.set(saved.item_id, card);
    });

    return Array.from(byItem.values());
  }, [partyInfo, savedByKey, optionByKey]);

  // What each line shows: a saved line what it copied, unless it asks to copy again; any other line the party's offer.
  const views = useMemo(
    () =>
      items.map((line: ICuttingLineInput): CuttingLineView & {
        materialId: number | null;
      } => {
        const key = cuttingLineKey(line);
        const saved = savedByKey.get(key);
        const entry = optionByKey.get(key);
        const keep = !!saved && !line.refresh;
        const received = saved?.received_qty || 0;
        const closed = !!saved?.closed;
        const qty = Number(line.qty) || 0;

        const cutLength = keep
          ? saved!.cut_length
          : entry?.size.cut_length ?? null;

        // The rate the line would copy: a saved line's own, else the party's offer. A user who may see rates types the
        // line's rate over it; another rate than that one is the order's own (3).
        const defaultRate = keep ? saved!.rate : entry?.size.rate;
        const defaultSource = keep
          ? saved!.rate_source
          : entry?.size.rate_source;
        const rate = withRates ? rateNumber(line.rate) ?? undefined : undefined;
        const typedOver =
          rate !== undefined &&
          defaultRate !== undefined &&
          Math.abs(rate - defaultRate) >= 0.005;
        const rateHint =
          !withRates || defaultRate === undefined
            ? null
            : typedOver
            ? `બદલેલ ભાવ • ${
                defaultSource === 3 ? "પહેલા" : rateSourceLabel(defaultSource)
              } ₹${formatAmount(defaultRate)}`
            : rate === undefined
            ? `${rateSourceLabel(defaultSource)} ₹${formatAmount(defaultRate)}`
            : rateSourceLabel(defaultSource);

        // An in-house party is offered every item, so one it isn't offered has changed on the item page.
        const problem = keep
          ? null
          : !entry
          ? partyInfoLoading
            ? null
            : partyInfo.inhouse
            ? "આ આઈટમ બદલાઈ છે, તેને કાઢી નાખો"
            : "આ પાર્ટી માટે આ આઈટમ નો કટીંગ ભાવ નથી અથવા આઈટમ બદલાઈ છે, તેને કાઢી નાખો"
          : entry.size.cut_length === null
          ? "આઈટમ માં કટીંગ લંબાઈ સેટ નથી, પહેલા આઈટમ માં કટીંગ લંબાઈ લખો"
          : null;

        // A saved line that nothing has been received of can copy the item page's new values again.
        const changes: string[] = [];

        if (
          saved &&
          entry &&
          !line.refresh &&
          received === 0 &&
          !closed &&
          entry.size.cut_length !== null
        ) {
          if (entry.size.cut_length !== saved.cut_length) {
            changes.push(
              `લંબાઈ ${formatLength(saved.cut_length, sizeUnit)} → ${formatLength(
                entry.size.cut_length,
                sizeUnit
              )}`
            );
          }

          if ((entry.option.material?.id ?? null) !== saved.material_id) {
            changes.push(
              `મટીરીયલ ${saved.material?.name || "-"} → ${
                entry.option.material?.name || "-"
              }`
            );
          }

          // A rate typed on the order was meant to differ from the item and party pages.
          if (
            withRates &&
            saved.rate_source !== 3 &&
            entry.size.rate !== undefined &&
            saved.rate !== undefined &&
            entry.size.rate !== saved.rate
          ) {
            changes.push(
              `ભાવ ₹${formatAmount(saved.rate)} → ₹${formatAmount(
                entry.size.rate
              )}`
            );
          }
        }

        return {
          key,
          categoryId:
            entry?.option.category?.id ??
            savedCategoryOfItem.get(line.item_id) ??
            0,
          code: entry?.option.code ?? saved?.item?.name ?? `#${line.item_id}`,
          sizeName: entry?.size.size_name ?? saved?.size?.name ?? null,
          imageUrl: entry?.option.image_url ?? saved?.item?.image_url ?? null,
          materialId: keep
            ? saved!.material_id
            : entry?.option.material?.id ?? null,
          materialName: keep
            ? saved!.material?.name ?? null
            : entry?.option.material?.name ?? null,
          cutLength,
          rate,
          rateSource: typedOver ? 3 : defaultSource,
          rateHint,
          received,
          closed,
          pieces: effectiveQty(qty, received, closed),
          problem,
          changes,
        };
      }),
    [
      items,
      savedByKey,
      savedCategoryOfItem,
      optionByKey,
      partyInfoLoading,
      partyInfo.inhouse,
      withRates,
      sizeUnit,
    ]
  );

  // A new order starts with one empty box; an order being edited with a box per category of its lines, in line order.
  const lineCategoryIds = useMemo(
    () => views.map((view) => view.categoryId),
    [views]
  );
  const { boxes, addBox, removeBox, changeBoxCategory, boxProblem } =
    useCategoryBoxes(
      () =>
        (defaultValues?.items || []).map(
          (line) => savedCategoryOfItem.get(line.item_id) ?? 0
        ),
      lineCategoryIds,
      remove
    );

  // Each material the lines cut, with the party's stock of it and the lines' pieces, plus any material that has bars
  // typed for it, so sent bars never go unseen.
  const needs = useMemo((): CuttingMaterialNeed[] => {
    const byMaterial = new Map<number, CuttingMaterialNeed>();
    const rowOf = (materialId: number, name: string | null) => {
      const row = byMaterial.get(materialId) || {
        materialId,
        name: name || materialById.get(materialId)?.name || `#${materialId}`,
        material: materialById.get(materialId),
        stock: partyInfo.stock.find((stock) => stock.material_id === materialId),
        lines: [],
        dropped: droppedBars.get(materialId) || [],
      };
      byMaterial.set(materialId, row);
      return row;
    };

    views.forEach((view) => {
      if (view.materialId) {
        rowOf(view.materialId, view.materialName).lines.push({
          key: view.key,
          name: lineName(view.code, view.sizeName),
          cutLength: view.cutLength || 0,
          qty: view.pieces,
          lineId: savedLineIds.get(view.key) ?? null,
        });
      }
    });

    Object.entries(materialInputs).forEach(([id, input]) => {
      if (Number(input.qty) > 0 && materialById.has(Number(id))) {
        rowOf(Number(id), null);
      }
    });

    return Array.from(byMaterial.values());
  }, [views, materialById, materialInputs, partyInfo, savedLineIds, droppedBars]);

  // A leftover is scrap below the order's limit (the edited order's own, else today's smallest cut length of any item)
  // or below its own smallest cut when that is smaller, as CuttingStock works it out.
  const scrapBelow = useMemo(() => {
    const cuts = views
      .map((view) => view.cutLength || 0)
      .filter((cut) => cut > 0);
    const own = cuts.length > 0 ? Math.min(...cuts) : null;
    const limit = partyInfo.scrap_below;

    return toUnits(
      limit === null ? own ?? 0 : own === null ? limit : Math.min(limit, own)
    );
  }, [views, partyInfo.scrap_below]);

  // What each material's card works out: the pieces used, the bars needed and sent, and how they are cut.
  const cuts = useMemo(
    () =>
      new Map(
        needs.map((row) => [
          row.materialId,
          materialCut(row, materialInputs[row.materialId], scrapBelow),
        ])
      ),
    [needs, materialInputs, scrapBelow]
  );

  const updateMaterial = (
    materialId: number,
    change: (input: CuttingMaterialInput) => Partial<CuttingMaterialInput>
  ) =>
    setMaterialInputs((current) => {
      const input = current[materialId] || noInput;

      return { ...current, [materialId]: { ...input, ...change(input) } };
    });

  // Unticking a piece leaves it with the party; ticking it again lets the order cut it.
  const handleToggleSkip = (materialId: number, length: number) =>
    updateMaterial(materialId, (input) => ({
      skip: input.skip.some((skipped) => toUnits(skipped) === toUnits(length))
        ? input.skip.filter((skipped) => toUnits(skipped) !== toUnits(length))
        : [...input.skip, length],
    }));

  // Another size refills the bars box with what the order needs of it.
  const handleSelectSize = (materialId: number, sizeId: number) =>
    updateMaterial(materialId, () => ({ sizeId, qty: null }));

  const handleChangeQty = (materialId: number, text: string | null) =>
    updateMaterial(materialId, () => ({ qty: text }));

  // Every category the form can name: the party's (only those it has something to cut in) and those of the saved lines.
  const categoryById = useMemo(() => {
    const categories = new Map<number, CuttingCategoryProps>();

    partyInfo.options.forEach((option) => {
      if (option.category) categories.set(option.category.id, option.category);
    });

    (savedOrder?.items || []).forEach((item) => {
      if (item.item?.category && !categories.has(item.item.category.id)) {
        categories.set(item.item.category.id, item.item.category);
      }
    });

    return categories;
  }, [partyInfo, savedOrder]);

  // The select offers the categories the party has something to cut in.
  const categoryOptions = useMemo((): CuttingCategoryOption[] => {
    const offered = new Map<number, CuttingCategoryProps>();

    partyInfo.options.forEach((option) => {
      if (option.category) offered.set(option.category.id, option.category);
    });

    return Array.from(offered.values())
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((category) => ({ value: category.id, label: categoryText(category) }));
  }, [partyInfo]);

  const categoryLabel = (categoryId: number | null) => {
    const category =
      categoryId !== null ? categoryById.get(categoryId) : undefined;

    return category ? categoryText(category) : null;
  };

  const handlePickerClose = (lines: ICuttingLineInput[]) => {
    setPickerCategoryId(null);

    const current = getValues("items");
    const same =
      lines.length === current.length &&
      lines.every(
        (line, index) =>
          cuttingLineKey(line) === cuttingLineKey(current[index]) &&
          line.qty === current[index].qty &&
          line.rate === current[index].rate &&
          line.refresh === current[index].refresh
      );

    if (!same) {
      replace(lines);
      clearErrors("items");
    }
  };

  // For a user who may see rates, copying the item again puts the party's current rate in the rate box, and undoing it
  // the saved one.
  const handleRefresh = (index: number, refresh: boolean) => {
    setValue(`items.${index}.refresh`, refresh, { shouldDirty: true });

    if (withRates) {
      const key = cuttingLineKey(getValues(`items.${index}`));
      const rate = refresh
        ? optionByKey.get(key)?.size.rate
        : savedByKey.get(key)?.rate;

      setValue(`items.${index}.rate`, rateText(rate), { shouldDirty: true });
    }
  };

  const canPick = !!partyId && !partyInfoLoading;
  const pickerLabel = !partyId
    ? "પહેલા પાર્ટી પસંદ કરો"
    : partyInfoLoading
    ? "આઈટમ લોડ થઈ રહી છે..."
    : "આઈટમ પસંદ કરો";

  const handleOnSubmit: SubmitHandler<ICuttingOrderFormInput> = (data) => {
    // Every box needs a category and an item of it; there is always a box, so an order without lines stops here.
    const boxError = boxProblem();

    if (boxError) {
      toast.error(boxError, { ...(toastStyles as any) });
      return;
    }

    const problem = views.find((view) => view.problem);

    if (problem) {
      toast.error(`${problem.code}: ${problem.problem}`, {
        ...(toastStyles as any),
      });
      return;
    }

    // The bars are checked against the party's pieces, which have to be loaded.
    if (partyInfoLoading) {
      toast.error("પાર્ટી ની વિગતો લોડ થઈ રહી છે, થોડી વાર પછી સાચવો", {
        ...(toastStyles as any),
      });
      return;
    }

    const short = needs.find((row) => cuts.get(row.materialId)?.problem);

    if (short) {
      toast.error(`${short.name}: ${cuts.get(short.materialId)?.problem}`, {
        ...(toastStyles as any),
      });
      return;
    }

    onSubmit(data, barsPayload(needs, cuts), skipPayload(needs, cuts));
  };

  return (
    <div className={formCardClass}>
      <div className={cx(cardContentClass, "pt-6")}>
        <form
          onSubmit={(event) => {
            setShowBoxErrors(true);
            handleSubmit(handleOnSubmit)(event);
          }}
          className="space-y-6"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="space-y-2 md:col-span-2">
              <label htmlFor="cutting-order-party" className={fieldLabelClass}>
                પાર્ટી પસંદ કરો <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.party_id && "error")}>
                <LuBuilding2 className={formSelectIconClass} />
                <Controller
                  control={control}
                  name="party_id"
                  rules={{ required: true, min: 1 }}
                  render={({ field }) => (
                    <Select
                      inputId="cutting-order-party"
                      ref={field.ref}
                      options={partyOptions}
                      value={
                        partyOptions.find(
                          (option) => option.value === field.value
                        ) || null
                      }
                      onChange={(option: any) => {
                        const value = option?.value || 0;

                        if (value === field.value) return;

                        field.onChange(value);
                        onPartyChange(value);

                        // Another party holds other pieces.
                        setMaterialInputs((current) =>
                          Object.fromEntries(
                            Object.entries(current).map(([id, input]) => [
                              id,
                              { ...input, skip: [] },
                            ])
                          )
                        );
                      }}
                      isDisabled={partyLocked}
                      placeholder="પાર્ટી સિલેક્ટ કરો..."
                      noOptionsMessage={() => "કટીંગ ની પાર્ટી મળી નથી"}
                      styles={iconSelectStyles}
                      isSearchable
                      className={cx(
                        "react-select-container",
                        errors.party_id && "error"
                      )}
                      classNamePrefix="react-select"
                    />
                  )}
                />
              </div>
              {errors.party_id ? (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  પાર્ટી સિલેક્ટ કરો
                </p>
              ) : partyLocked ? (
                <p className="text-xs text-gray-500">
                  આ ઓર્ડર ની રિસીવ થઈ ગઈ હોવાથી પાર્ટી બદલી શકાશે નહીં
                </p>
              ) : (
                partyChanged && (
                  <p className="text-xs text-orange-700">
                    બીજી પાર્ટી પસંદ કરતાં દરેક આઈટમ ની લંબાઈ અને ભાવ નવી પાર્ટી મુજબ લાગશે
                  </p>
                )
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="cutting-order-date" className={fieldLabelClass}>
                તારીખ અને સમય <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.date && "error")}>
                <LuDatabase className={formSelectIconClass} />
                <Input
                  id="cutting-order-date"
                  type="datetime-local"
                  className={fieldInputClass(!!errors.date)}
                  {...register("date", { required: true })}
                />
                {errors.date && (
                  <LuAlertCircle className={fieldErrorIconClass} />
                )}
              </div>
              {errors.date && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  તારીખ અને સમય દાખલ કરો
                </p>
              )}
            </div>
          </div>

          <CuttingOrderLines
            control={control}
            fields={fields}
            errors={errors}
            views={views}
            boxes={boxes}
            categoryOptions={categoryOptions}
            categoryLabel={categoryLabel}
            categoriesMessage={
              !partyId
                ? "પહેલા પાર્ટી પસંદ કરો"
                : partyInfoLoading
                ? "કેટેગરી લોડ થઈ રહી છે..."
                : "આ પાર્ટી માટે કટીંગ ની કોઈ આઈટમ નથી"
            }
            showBoxErrors={showBoxErrors}
            onAddBox={addBox}
            onRemoveBox={removeBox}
            onChangeBoxCategory={changeBoxCategory}
            onRemove={remove}
            onOpenPicker={setPickerCategoryId}
            onRefresh={handleRefresh}
            refreshed={items.map((item) => !!item.refresh)}
            canPick={canPick}
            pickerLabel={pickerLabel}
            withRates={withRates}
            sizeUnit={sizeUnit}
          />

          <CuttingMaterialsSent
            needs={needs}
            cuts={cuts}
            onToggleSkip={handleToggleSkip}
            onSelectSize={handleSelectSize}
            onChangeQty={handleChangeQty}
            later={!!savedOrder && partyInfo.later}
            sizeUnit={sizeUnit}
            isLoading={partyInfoLoading}
          />

          <div className="flex flex-col md:flex-row md:items-center md:justify-end gap-4 pt-4">
            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                રદ કરો
              </Button>

              <button
                type="submit"
                className={gradientSubmitButtonClass}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className={buttonSpinnerClass}></div>
                    સાચવી રહ્યા છીએ...
                  </div>
                ) : (
                  submitLabel
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      {pickerCategoryId !== null && (
        <CuttingItemPickerModal
          cards={cards.filter((card) => card.categoryId === pickerCategoryId)}
          cardsLoading={partyInfoLoading}
          categoryLabel={categoryLabel(pickerCategoryId) ?? undefined}
          initialLines={getValues("items")}
          withRates={withRates}
          inhouse={partyInfo.inhouse}
          sizeUnit={sizeUnit}
          onClose={handlePickerClose}
        />
      )}
    </div>
  );
};

export default CuttingOrderForm;
